import {
  AddToQueue,
  Bungalow,
  HealthAndSafety,
  LocalHospital,
  MedicalInformation,
  MedicalInformationOutlined,
  MedicalServices,
  Medication,
  People,
  Sick,
} from "@mui/icons-material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import CarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import MicIcon from "@mui/icons-material/MicOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import PlaylistPlayOutlinedIcon from "@mui/icons-material/PlaylistPlayOutlined";
import SchoolIcon from "@mui/icons-material/School";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { Menu, MenuItem, ProSidebar } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { Link } from "react-router-dom";
import { tokens } from "../../theme";

// ---------------------------------------------------------------------------
// Role ID constants (matches API response)
// ---------------------------------------------------------------------------
const ROLES = {
  SUPER_ADMIN: 2,
  SYS_ADMIN: 3,
  MEMBER_MGR: 4,
  EVENT_MGR: 5,
  TRAIN_MGR: 6,
  WORSHIP_MGR: 7,
  HEALTH_MGR: 8,
  COMMS_MGR: 9,
};

// ---------------------------------------------------------------------------
// Menu item definitions grouped by category
// ---------------------------------------------------------------------------
const MENU_GROUPS = {
  // 1. General & Administrative Management
  general: [
    {
      title: "Dashboard",
      to: "/dashboard",
      icon: <HomeOutlinedIcon />,
    },
    {
      title: "CMS Management",
      to: "/cms",
      icon: <SettingsOutlinedIcon />,
    },
    {
      title: "User Management",
      to: "/user",
      icon: <PeopleOutlinedIcon />,
    },
    {
      title: "Subscription Options",
      to: "/subscription",
      icon: <ContactsOutlinedIcon />,
      children: [
        { title: "Subscribed Users", to: "/subscribedusers" },
      ],
    },
    {
      title: "Order Management",
      to: "/ordermanagement",
      icon: <ReceiptOutlinedIcon />,
    },
    {
      title: "Seller Payouts",
      to: "/sellerpayouts",
      icon: <PaidOutlinedIcon />,
    },
    {
      title: "Live Stream Ratings",
      to: "/livestreamratings",
      icon: <StarOutlineIcon />,
    },
    {
      title: "Training Documents",
      to: "/trainingdocs",
      icon: <SchoolOutlinedIcon />,
    },
    {
      title: "Payment Logs",
      to: "/paymentlogs",
      icon: <PaymentOutlinedIcon />,
    },
    {
      title: "Donations",
      to: "/donations",
      icon: <RequestQuoteIcon />,
    },
    {
      title: "Invoices",
      to: "/invoices",
      icon: <RequestQuoteIcon />,
    },
    {
      title: "Tour Cost",
      to: "/tourCost",
      icon: <RequestQuoteIcon />,
    },
    {
      title: "Fee Schedules for Hospital Services",
      to: "/feeSchedulesForHospitalServicesManagement",
      icon: <LocalHospital />,
    },
    {
      title: "Fee Schedules for Medication",
      to: "/feeScheduleForMedicationManagement",
      icon: <Medication />,
    },
    {
      title: "Fee Schedules for Funeral Home",
      to: "/feeSchedulesForFuneralHomeManagement",
      icon: <Bungalow />,
    },
  ],

  // 2. Membership & Career Management
  membership: [
    {
      title: "Membership Card Management",
      to: "/membership",
      icon: <SettingsOutlinedIcon />,
    },
    {
      title: "Careers",
      to: "/careers",
      icon: <BusinessCenterOutlinedIcon />,
      children: [
        { title: "Career repliers from users", to: "/careers/careerrepliers" },
      ],
    },
    {
      title: "Feedbacks",
      to: "/feedback",
      icon: <FeedbackOutlinedIcon />,
    },
  ],

  // 3. Events & Activities
  events: [
    {
      title: "Event Management",
      to: "/event",
      icon: <EmojiEventsOutlinedIcon />,
    },
    {
      title: "Pre Tour Registrations",
      to: "/tour",
      icon: <CarOutlinedIcon />,
      children: [
        { title: "Tour Registrations", to: "/tour/register" },
      ],
    },
    {
      title: "AfyaBora Bookings",
      to: "/afyaborabookings",
      icon: <LocalPharmacyIcon />,
    },
    {
      title: "Vote Management",
      to: "/vote-management",
      icon: <HowToVoteIcon />,
    },
    {
      title: "Dedication Logs",
      to: "/dedication",
      icon: <CategoryOutlinedIcon />,
    },
    {
      title: "Appointment Logs",
      to: "/appointment",
      icon: <EventAvailableOutlinedIcon />,
    },
    {
      title: "Activity Report",
      to: "/activityreport",
      icon: <AnalyticsIcon />,
    },
  ],

  // 4. Training & Educational Content
  training: [
    {
      title: "Course Management",
      to: "/courses",
      icon: <SchoolIcon />,
      children: [
        { title: "Module Management", to: "/module" },
      ],
    },
    {
      title: "Templates",
      to: "/templates",
      icon: <PlaylistAddCheckOutlinedIcon />,
    },
  ],

  // 5. Worship & Spiritual Production
  worship: [
    {
      title: "Worship Centers",
      to: "/worship-centers",
      icon: <GroupOutlinedIcon />,
    },
    {
      title: "Worship Service Management",
      to: "/worship-service-management",
      icon: <SettingsOutlinedIcon />,
    },
    {
      title: "Sermons Management",
      to: "/sermons",
      icon: <PlaylistPlayOutlinedIcon />,
    },
    {
      title: "Artist Management",
      to: "/artist-management",
      icon: <BusinessCenterOutlinedIcon />,
    },
    {
      title: "Video Management",
      to: "/video-management",
      icon: <VideoLibraryOutlinedIcon />,
    },
    {
      title: "Audio Management",
      to: "/audio-management",
      icon: <MicIcon />,
    },
  ],

  // 6. Health & Medical Services
  health: [
    {
      title: "Prescription",
      to: "/prescription",
      icon: <LocalPharmacyIcon />,
    },
    {
      title: "Hospitals And Health Centers",
      to: "/hospitalsAndHealthCenters",
      icon: <LocalHospital />,
    },
    {
      title: "Pharmacy",
      to: "/pharmacyManagement",
      icon: <MedicalInformation />,
    },
    {
      title: "Covered Care Services",
      to: "/coveredCareServicesManagement",
      icon: <People />,
    },
    {
      title: "Doctors",
      to: "/doctorManagement",
      icon: <HealthAndSafety />,
    },
    {
      title: "Specialist Doctor",
      to: "/specialistDoctorManagement",
      icon: <MedicalServices />,
    },
    {
      title: "Covered Medications And Drugs",
      to: "/coveredMedicationsAndDrugsManagement",
      icon: <MedicalInformationOutlined />,
    },
    {
      title: "Maladies And Conditions",
      to: "/maladiesAndConditionsManagement",
      icon: <Sick />,
    },
    {
      title: "Burial Expenses Claim",
      to: "/burialexpensesclaim",
      icon: <AccountBalanceIcon />,
    },
    {
      title: "Funerals and Mortuaries Homes",
      to: "/funeralsAndMortuariesHomesManagement",
      icon: <AddToQueue />,
    },
  ],

  // 7. Communication & Information
  communications: [
    {
      title: "News",
      to: "/news",
      icon: <MicIcon />,
    },
    {
      title: "Blogs",
      to: "/blogs",
      icon: <MapOutlinedIcon />,
    },
    {
      title: "Forms Submitted By User",
      to: "/formsuser",
      icon: <ListAltOutlinedIcon />,
      children: [
        { title: "New Service Requests", to: "/newserviceuser" },
      ],
    },
    {
      title: "Contact Us",
      to: "/contactUs",
      icon: <PhoneIcon />,
    },
  ],

  // Extra items only for Super Admin
  adminOnly: [
    {
      title: "Admins",          // ← NEW
      to: "/admins",
      icon: <PeopleOutlinedIcon />,
    },
    {
      title: "User Stripe Account",
      to: "/userstripe",
      icon: <MapOutlinedIcon />,
    },
    {
      title: "Add Post",
      to: "/addpost",
      icon: <MapOutlinedIcon />,
    },
  ],
};

// ---------------------------------------------------------------------------
// Which groups each role can see
// Super Admin (2) gets everything — handled separately below
// ---------------------------------------------------------------------------
const ROLE_GROUPS = {
  [ROLES.SYS_ADMIN]:   ["general"],
  [ROLES.MEMBER_MGR]:  ["membership"],
  [ROLES.EVENT_MGR]:   ["events"],
  [ROLES.TRAIN_MGR]:   ["training"],
  [ROLES.WORSHIP_MGR]: ["worship"],
  [ROLES.HEALTH_MGR]:  ["health"],
  [ROLES.COMMS_MGR]:   ["communications"],
};

// Returns the flat ordered list of menu items for a given roleId
function getMenuItemsForRole(roleId) {
  const rid = Number(roleId);

  // Super Admin always gets every group in order
  if (rid === ROLES.SUPER_ADMIN) {
    return [
      ...MENU_GROUPS.general,
      ...MENU_GROUPS.membership,
      ...MENU_GROUPS.events,
      ...MENU_GROUPS.training,
      ...MENU_GROUPS.worship,
      ...MENU_GROUPS.health,
      ...MENU_GROUPS.communications,
      ...MENU_GROUPS.adminOnly,
    ];
  }

  const groups = ROLE_GROUPS[rid];
  if (!groups) return [MENU_GROUPS.general[0]]; // fallback: dashboard only

  // Every role always gets the Dashboard (first item of general)
  const dashboardItem = MENU_GROUPS.general[0];
  const items = groups.flatMap((g) => MENU_GROUPS[g] ?? []);

  // Prepend dashboard if not already included
  if (!items.find((i) => i.to === "/dashboard")) {
    return [dashboardItem, ...items];
  }
  return items;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const Item = ({ title, to, icon, selected, setSelected, children }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

  return (
    <>
      <MenuItem
        active={selected === title}
        style={{ color: colors.grey[100] }}
        onClick={() => {
          setSelected(title);
          if (children) setIsSubmenuOpen((prev) => !prev);
        }}
        icon={icon}
      >
        <Typography>{title}</Typography>
        <Link to={to} />
      </MenuItem>
      {isSubmenuOpen && children}
    </>
  );
};

const Subtopic = ({ title, to, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <MenuItem
      active={selected === title}
      style={{ color: colors.grey[100], textAlign: "center" }}
      onClick={() => setSelected(title)}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

// ---------------------------------------------------------------------------
// Main Sidebar component
// ---------------------------------------------------------------------------
const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");

  // Read role from localStorage (set during login)
  const roleId = Number(localStorage.getItem("adminRoleId")) || ROLES.SUPER_ADMIN;
  const roleName = localStorage.getItem("roleName") || "Admin";

  const menuItems = getMenuItemsForRole(roleId);

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "10px 30px 5px 10px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "30px 20px 20px 20px",
              color: colors.grey[100],
              textAlign: "center",
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="30px"
              >
                <Typography variant="h3" color={colors.grey[100]}>
                  JCGN
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          {/* Role label */}
          {!isCollapsed && (
            <Box textAlign="center" mb="10px">
              <Typography
                variant="caption"
                color={colors.greenAccent[400]}
                sx={{ textTransform: "uppercase", letterSpacing: "1px" }}
              >
                {roleName}
              </Typography>
            </Box>
          )}

          {/* Dynamic menu items */}
          <Box
            pl={!isCollapsed ? "20px" : undefined}
            alignItems="left"
            sx={{ marginTop: "10px" }}
            width={!isCollapsed ? "calc(100% + 40px)" : undefined}
          >
            {menuItems.map((item) => (
              <Item
                key={item.title}
                title={item.title}
                to={item.to}
                icon={item.icon}
                selected={selected}
                setSelected={setSelected}
              >
                {item.children &&
                  item.children.map((child) => (
                    <Subtopic
                      key={child.title}
                      title={child.title}
                      to={child.to}
                      selected={selected}
                      setSelected={setSelected}
                    />
                  ))}
              </Item>
            ))}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;